import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpErrorResponse, HttpHeaders, HttpContextToken } from '@angular/common/http';
import { HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, defer, from } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { FirebaseService } from './firebase.service';
import { FunctionsService } from '../data/functions.service';
import { environment } from 'src/environments/environment';

/**
 * Opt-in por requisição para pular o `alert()`/retentativa genéricos deste
 * interceptor. Existe para chamadas de ENRIQUECIMENTO visual — que não podem
 * travar a tela nem assustar o usuário com um erro que não é dele (ex.: a
 * ficha do vendedor no modal "Avaliar Vendedor", T-156). Default `false`:
 * não muda nada para o resto do app, que continua vendo o alerta de sempre.
 */
export const SILENT_HTTP_ERRORS = new HttpContextToken<boolean>(() => false);

/**
 * Complemento de `SILENT_HTTP_ERRORS` para chamadas autenticadas: mantém só o
 * refresh de token em 401 (sessão expirada), sem alert nem retentativa nos
 * demais erros — que a tela trata sozinha.
 */
export const KEEP_AUTH_REFRESH = new HttpContextToken<boolean>(() => false);

@Injectable({
  providedIn: 'root'
})
export class HttpInterceptorService implements HttpInterceptor {

  /** Aguarda o  retorno do serviço de token */
  private autenticationInProgress = false;

  /* Observa o comportamento do token, pode ser nulo caso não exista nenhum */
  private authenticationTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  constructor(
    public dialog: MatDialog,
    private router: Router,
    private firebase: FirebaseService,
    private functions: FunctionsService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    request = this.handleAuthorizationHeader(request);
    const silent = request.context.get(SILENT_HTTP_ERRORS);

    return next.handle(request)
      .pipe(catchError(error => {
        const manterAuth = request.context.get(KEEP_AUTH_REFRESH);
        if (silent && !(manterAuth && error instanceof HttpErrorResponse && error.status === 401)) {
          // Chamada marcada como silenciosa: repassa o erro cru, sem alert,
          // sem retentativa automática, sem o fluxo de token expirado — quem
          // assinou a chamada decide como (ou se) degrada.
          return throwError(() => error);
        }
        if (error instanceof HttpErrorResponse) {
          if (error.status === 303) {
            // See Other
            return throwError(() => error);
          } else if (error.status <= 399 ) {
            /** Há possibilidade de instabilidade na rede do client */
            alert(`Ocorreu um erro ao processar a solicitação.
            Certifique-se que há conexão com a internet e caso o erro persista, tente novamente mais tarde. (${error.status})`);
            return throwError(() => error);
          } else if (error.status === 401) {
            /** Tokens expirados, fazer o refresh do token caso a sessão seja válida */
            return this.handleUnnauthorized(request, next);
          } else if (error.status === 422) {
            /** Ameaça detectada */
            alert(`Falha na solicitação, entre em contato com o atendimento. (${error.status})`);
            return throwError(() => error);
          } else if (error.status <= 499) {
            /** Erros de negócio, apenas repassar o erro */
            return throwError(() => error);
          } else if (error.status === 500) {
            /** Erro genérico */

            /** Retentativa automática devido intermitências */
            console.error('Retentativa Automática', error.error, error.url);
            return next.handle(request)
              .pipe(catchError(error => {
                alert(`Houve um erro ao processar a solicitação. Por favor, refaça a operação.(${error.status}).`);
                return throwError(() => error);
              }));
          } else if (error.status === 502) {
            /** Indisponibilidade do Protheus via Broker retorna 502 */
            alert(`Houve uma intermitência em nossos sistemas, por favor tente de novo em instantes (${error.status}),`);
            return throwError(() => error);
          } else if (error.status === 504) {
            /** Sensedia está devolvendo 504 quando ocorre timeout */
            alert(`A solicitação demorou mais que o esperado. Por favor, refaça a operação (${error.status}),`);
            return throwError(() => error);
          } else if (error.status <= 599) {
            /** Outras possiveis falhas não especificadas */
            alert(`Houve uma falha ao processar a solicitação. Por favor, refaça a operação (${error.status}).`);
            return throwError(() => error);
          } else {
            alert(`Houve um erro não esperado ao processar a solicitação. (${error.status})`);
            return throwError(() => error);
          }
        } else {
          alert(`Ocorreu um erro não identificado`);
          return throwError(() => error);
        }
      }));
  }

  private handleUnnauthorized(request: HttpRequest<any>, next: HttpHandler) {

    /* Enfileira todas as requisições caso esteja em processo de renovação de token */
    if (this.autenticationInProgress) {
      return this.authenticationTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(() => {
          return next.handle(this.handleAuthorizationHeader(request));
        })
      );
    } else {
      if (!this.firebase.user) {
        this.unnauthorized();
        return throwError(() => new HttpErrorResponse({ status: 401, url: request.url }));
      }

      this.autenticationInProgress = true;
      this.authenticationTokenSubject.next(null);

      const tokenObservable = defer(() => from(this.firebase.user.getIdToken(true)));

      return tokenObservable.pipe(
        switchMap((token: any) => {
          if (token) {
            this.firebase.entrar(token)
            return this.functions.profile().pipe(
              switchMap((profile: any) => {
                console.info(profile)
                this.firebase.profile = profile.data;
                this.authenticationTokenSubject.next('sucesso');
                this.autenticationInProgress = false;
                return next.handle(this.handleAuthorizationHeader(request));
              })
            )
          } else {
            this.unnauthorized();
            return next.handle(request);
          }
        })
      )
    }
  }

  private handleAuthorizationHeader(request: HttpRequest<any>) {

    var token = null;

    if (request.url.match(environment.cloud_functions)) {
      token = this.firebase.token();
    } else if (request.url.match(environment.storage)) {
      console.info(this.firebase?.profile)
      token = this.firebase?.profile?.access_token
    }

    if (!token || token === 'null' || token === 'undefined') {
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private unnauthorized() {

    alert('Sessão expirada. Por favor, faça o login novamente.');

    this.router.navigate(['acesso'], { queryParams: { retorno: this.router.url } });

  }
}
